import React, { useState, useEffect } from "react";
import {
  Paper,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  CircularProgress,
  Box,
} from "@material-ui/core";
import {
  VideoCall,
  Schedule,
  CalendarToday,
  AccessTime,
  Person,
} from "@material-ui/icons";
import Button from "../../../../components/EcommerceActionButton/Button";
import { useGetTrainingSessions } from "../../../../utils/react-query/querySalesperson";
import {
  formatDistanceToNow,
  format,
  isPast,
  isWithinInterval,
  addMinutes,
  differenceInMinutes,
  isSameDay,
} from "date-fns";
import { es } from "date-fns/locale";
import {
  SessionStatusInfo,
  CalendlyEvent,
  TrainingSession,
} from "../../../../types/training";
import styles from "./TrainingSessions.module.scss";

// Helper function to transform CalendlyEvent to TrainingSession
const mapCalendlyEventToTrainingSession = (
  event: CalendlyEvent
): TrainingSession => {
  const firstMembership = event.event_memberships[0];
  const duration = differenceInMinutes(
    new Date(event.end_time),
    new Date(event.start_time)
  );

  return {
    id: event.uri,
    title: event.name,
    description: event.meeting_notes_plain || "",
    scheduledAt: event.start_time,
    endTime: event.end_time,
    duration,
    meetingLink: event.location?.join_url || "",
    status:
      event.status === "active"
        ? "active"
        : event.status === "canceled"
        ? "canceled"
        : "completed",
    eventType: event.event_type,
    trainer: {
      name: firstMembership?.user_name || "",
      email: firstMembership?.user_email || "",
    },
    locationType: event.location?.type || "",
    metadata: {
      calendlyUri: event.uri,
      eventTypeUri: event.event_type,
    },
  };
};

const TrainingSessions: React.FC = () => {
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  // Use react-query hook
  const { data, isLoading } = useGetTrainingSessions();
  const sessions: TrainingSession[] = (data?.collection || []).map(
    mapCalendlyEventToTrainingSession
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const getSessionDates = (session: TrainingSession) => {
    const sessionDate = new Date(session.scheduledAt);
    const sessionEndDate = new Date(session.endTime);
    const fifteenMinutesBefore = addMinutes(sessionDate, -15);
    return { sessionDate, sessionEndDate, fifteenMinutesBefore };
  };

  const getSessionStatus = (session: TrainingSession): SessionStatusInfo => {
    const { sessionDate, sessionEndDate, fifteenMinutesBefore } =
      getSessionDates(session);
    const now = currentTime;

    // Check if session is currently in progress
    if (isWithinInterval(now, { start: sessionDate, end: sessionEndDate })) {
      return { label: "¡En Curso!", color: "primary", priority: 1 };
    }

    // Check if session is about to start (within 15 minutes)
    if (
      isWithinInterval(now, { start: fifteenMinutesBefore, end: sessionDate })
    ) {
      return { label: "¡Próxima a iniciar!", color: "secondary", priority: 2 };
    }

    // Check if session is today
    if (isSameDay(sessionDate, now) && !isPast(sessionDate)) {
      return { label: "Hoy", color: "secondary", priority: 3 };
    }

    return { label: "Próximamente", color: "default", priority: 4 };
  };

  const getCardClassName = (session: TrainingSession): string => {
    const status = getSessionStatus(session);
    if (status.priority === 1)
      return `${styles.sessionCard} ${styles.urgentSession}`;
    if (status.priority === 2)
      return `${styles.sessionCard} ${styles.upcomingSession}`;
    if (status.priority === 3)
      return `${styles.sessionCard} ${styles.todaySession}`;
    return `${styles.sessionCard} ${styles.normalSession}`;
  };

  const getChipClassName = (session: TrainingSession): string => {
    const status = getSessionStatus(session);
    if (status.priority === 1) return styles.chipUrgent;
    if (status.priority === 2) return styles.chipUpcoming;
    if (status.priority === 3) return styles.chipToday;
    return styles.chipDefault;
  };

  const canJoinSession = (session: TrainingSession): boolean => {
    const { sessionEndDate, fifteenMinutesBefore } = getSessionDates(session);
    const now = currentTime;

    return isWithinInterval(now, {
      start: fifteenMinutesBefore,
      end: sessionEndDate,
    });
  };

  const handleJoinSession = (meetingLink: string): void => {
    window.open(meetingLink, "_blank", "noopener,noreferrer");
  };

  const formatSessionDate = (dateString: string): string => {
    const date = new Date(dateString);
    return format(date, "EEEE, d 'de' MMMM 'de' yyyy", { locale: es });
  };

  const formatSessionTime = (dateString: string): string => {
    const date = new Date(dateString);
    return format(date, "HH:mm", { locale: es });
  };

  const getTimeUntilSession = (scheduledAt: string): string | null => {
    const sessionDate = new Date(scheduledAt);
    if (isPast(sessionDate)) return null;

    return formatDistanceToNow(sessionDate, {
      locale: es,
      addSuffix: true,
    });
  };

  if (isLoading) {
    return (
      <section className="layout-dashboard-container">
        <h4 className="header-title">Capacitaciones Programadas</h4>
        <Box className={styles.loadingContainer}>
          <CircularProgress />
        </Box>
      </section>
    );
  }

  return (
    <section className="layout-dashboard-container">
      <div className={styles.sessionsContainer}>
        {sessions.length === 0 ? (
          <Paper className={styles.emptyState}>
            <CalendarToday
              style={{ fontSize: 64, marginBottom: 16, opacity: 0.3 }}
            />
            <Typography variant="h6" gutterBottom>
              No hay capacitaciones programadas
            </Typography>
            <Typography variant="body2">
              Las próximas capacitaciones aparecerán aquí cuando sean agendadas.
            </Typography>
          </Paper>
        ) : (
          sessions.map((session) => {
            const status = getSessionStatus(session);
            const timeUntil = getTimeUntilSession(session.scheduledAt);
            const canJoin = canJoinSession(session);

            return (
              <Card key={session.id} className={getCardClassName(session)}>
                <CardContent>
                  <div className={styles.sessionHeader}>
                    <div style={{ flex: 1 }}>
                      <Typography variant="h6" className={styles.sessionTitle}>
                        {session.title}
                      </Typography>
                      <Chip
                        label={status.label}
                        size="small"
                        className={`${styles.statusChip} ${getChipClassName(
                          session
                        )}`}
                      />
                    </div>
                  </div>

                  {session.description && (
                    <Typography
                      variant="body1"
                      className={styles.sessionDescription}
                    >
                      {session.description}
                    </Typography>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <div className={styles.sessionInfo}>
                        <Person fontSize="small" />
                        <Typography variant="body2">
                          <strong>Instructor:</strong> {session.trainer.name}
                        </Typography>
                      </div>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <div className={styles.sessionInfo}>
                        <CalendarToday fontSize="small" />
                        <Typography variant="body2">
                          {formatSessionDate(session.scheduledAt)}
                        </Typography>
                      </div>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <div className={styles.sessionInfo}>
                        <AccessTime fontSize="small" />
                        <Typography variant="body2">
                          {formatSessionTime(session.scheduledAt)} (
                          {session.duration || 60} min)
                        </Typography>
                      </div>
                    </Grid>
                  </Grid>

                  {timeUntil && (
                    <Box
                      className={`${styles.countdown} ${
                        status.priority === 1
                          ? styles.urgentCountdown
                          : status.priority === 2 || status.priority === 3
                          ? styles.todayCountdown
                          : ""
                      }`}
                    >
                      <Schedule fontSize="small" />
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {status.priority === 1 ? "¡Comienza " : "Comienza "}
                        {timeUntil}
                      </Typography>
                    </Box>
                  )}

                  <Box className={styles.buttonContainer}>
                    <Button
                      color="primary"
                      shape="little-rounded"
                      disabled={!canJoin}
                      icon={VideoCall}
                      onClick={() => handleJoinSession(session.meetingLink)}
                    >
                      {canJoin
                        ? "Unirse a la Videollamada"
                        : "Disponible 15 minutos antes"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </section>
  );
};

export default TrainingSessions;
